import { ensureDefined } from '@avantia/client-and-server-utilities';
import {
  createControlId,
  HpAddressItemResponse,
  HpAddressLookupActionProps,
  HpAddressLookupResponse
} from '@avantia/questionset-model';
import { HpClientDebugFlags } from '../clientDebugFlags';
import { logWarning } from '../clientLogging';
import { registerConfigurationAction } from '../config/registerClientConfig';
import { getAnswerValue } from '../tools/utilities';
import {
  HpAddressLookupAction,
  HpAddressLookupFailedPayload,
  HpAddressLookupPayload,
  HpCreateActionPromisesProps,
  HpDependentActionFunction,
  HpDispatchFunction,
  HpGetAddressLookupActionProps
} from '../types';
import { HpDynamicButtonClickPayload } from './dynamicButtonClickActionTypes';
import { fetchResolver } from './fetchResolver';
import { promiseVoidToFalse } from './promiseVoidToFalse';

let websiteEndpoint: string;
registerConfigurationAction((config) => {
  ({ websiteEndpoint } = config);
});

export function searchForAddresses(
  properties: HpCreateActionPromisesProps
): HpDependentActionFunction<HpAddressLookupPayload> | undefined {
  const { dependentAction, payload, question, questionSet } = properties;
  const { props } = ensureDefined(dependentAction, 'dependentAction');
  if (!payload) {
    ensureDefined(payload, 'payload');
  }

  const { groupNumber } = payload as HpDynamicButtonClickPayload;
  const { answers: answerMapDefined } = questionSet;
  const { searchFields, lookupResultsId, lookupButtonId, noResultsFoundMessage, errorLoadingAddressesMessage } =
    ensureDefined(props as HpAddressLookupActionProps, 'props');
  const answers = searchFields.map((f) => {
    if (groupNumber) {
      const id = createControlId({ groupNumber, questionId: f, options: { noPrefix: true } });
      return answerMapDefined[id];
    }

    return answerMapDefined[f];
  });
  let searchTerms: (string | undefined)[] | undefined;
  const { textAndStyle } = question.data;
  ensureDefined(lookupResultsId, 'lookupResultsId');
  ensureDefined(lookupButtonId, 'lookupButtonId');
  if (answers) {
    searchTerms = answers.map((answer) => {
      const { answerValue } = getAnswerValue(answer, question, 'request');
      return typeof answerValue === 'string' ? answerValue : undefined;
    });

    const searchTerm = searchTerms.filter((x) => !!x).join(' ');
    return getAddressLookupAction({
      searchTerm,
      questionId: question.questionId,
      lookupResultsId,
      lookupButtonId,
      noResultsFoundMessage,
      errorLoadingAddressesMessage,
      searchFields,
      groupNumber
    });
  }

  logWarning(
    `The "address-lookup" action could not be performed ${
      !textAndStyle
        ? 'because the "textAndStyle" property is undefined'
        : !searchTerms || searchTerms.length === 0
        ? `because the "searchFields" answers (${searchFields.join(', ')}) do not map to any answers`
        : 'and the reason is not immediately obvious'
    }.`,
    HpClientDebugFlags.None
  );
}

function getAddressLookupAction({
  searchTerm,
  questionId,
  lookupResultsId,
  lookupButtonId,
  noResultsFoundMessage,
  errorLoadingAddressesMessage,
  searchFields,
  groupNumber
}: HpGetAddressLookupActionProps): HpDependentActionFunction<HpAddressLookupPayload> {
  const emptyAction: HpAddressLookupAction = {
    type: 'ADDRESS_LOOKUP_RETRIEVED',
    payload: { searchTerm: '', questionId, addresses: [], lookupResultsId, lookupButtonId, searchFields, groupNumber }
  };
  const addressFailedPayload: HpAddressLookupFailedPayload = {
    lookupButtonId,
    errorLoadingAddressesMessage:
      errorLoadingAddressesMessage ||
      "Sorry, we're having problems loading the address. Please try again, or enter your address manually.",
    groupNumber
  };
  return (dispatch: HpDispatchFunction<HpAddressLookupPayload>): Promise<boolean> => {
    if (!searchTerm) {
      dispatch(emptyAction);
      // The 'false' says don't perform the default action after this completes.
      return Promise.resolve(false);
    }

    dispatch({ type: 'ADDRESS_LOOKUP_CALL_STARTED', payload: emptyAction.payload });
    const url = `${websiteEndpoint}/api/address-lookup/${encodeURIComponent(searchTerm)}`;
    return promiseVoidToFalse(
      fetchResolver<never, HpAddressLookupPayload>({
        options: { url, method: 'GET', description: `Search for addresses using term "${searchTerm}"` },
        loadingPageType: 'overlay',
        showErrorPage: false,
        dispatch,
        successAction: (payload) => {
          const { success, type, addresses } = payload as unknown as HpAddressLookupResponse;
          if (success === true) {
            if (type === 'address') {
              const address = payload as unknown as HpAddressItemResponse;
              dispatch({
                type: 'ADDRESS_ITEM_RETRIEVED',
                payload: {
                  searchTerm,
                  questionId,
                  addresses: [],
                  address,
                  lookupResultsId,
                  lookupButtonId,
                  searchFields,
                  groupNumber
                }
              });
            } else {
              dispatch({
                type: 'ADDRESS_LOOKUP_RETRIEVED',
                payload: {
                  searchTerm,
                  questionId,
                  addresses,
                  lookupResultsId,
                  lookupButtonId,
                  noResultsFoundMessage,
                  searchFields,
                  groupNumber
                }
              });
            }
          } else {
            dispatch({
              type: 'ADDRESS_LOOKUP_FAILED',
              payload: addressFailedPayload as any
            });
          }
        },
        failedAction: () => {
          dispatch({
            type: 'ADDRESS_LOOKUP_FAILED',
            payload: addressFailedPayload as any
          });
        }
      })
    );
  };
}
